import React, { useEffect, useState } from "react";

export const EditProductModal = ({ mode, product, onSubmit, onClose }) => {
  const [value, setValue] = useState("0");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const initialValue =
      mode === "price"
        ? Number(product.current_price || 0).toFixed(2)
        : String(product.available ?? 0);
    setValue(initialValue);
    setError(null);
  }, [mode, product]);

  const title = mode === "price" ? "Update price" : "Update quantity";
  const label = mode === "price" ? "Price (PLN)" : "Quantity";
  const description =
    mode === "price"
      ? `Set a new price for "${product.name}"`
      : `Set available quantity for "${product.name}"`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    const normalized = value.replace(",", ".").trim();

    if (normalized.length === 0) {
      setError(mode === "price" ? "Provide a price value." : "Provide a quantity value.");
      return;
    }

    let numeric;
    if (mode === "price") {
      numeric = Number(normalized);
      if (!Number.isFinite(numeric) || numeric < 0) {
        setError("Provide a valid non-negative price.");
        return;
      }
    } else {
      numeric = Number(normalized);
      if (!Number.isInteger(numeric) || numeric < 0) {
        setError("Provide a non-negative whole number.");
        return;
      }
    }

    setSubmitting(true);
    try {
      await onSubmit(numeric);
    } catch (err) {
      setError(err.message || "Failed to update the offer.");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const closeOnBackdrop = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm px-4"
      onMouseDown={closeOnBackdrop}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
            {label}
            <input
              type="text"
              inputMode={mode === "price" ? "decimal" : "numeric"}
              autoFocus
              value={value}
              onChange={(event) => setValue(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </label>

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
